import { useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import sendMessageIcon from '~/assets/icons/send_message.svg';
import { playSound, Sound } from '~/assets/sounds';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction } from '~/shared';
import {
	StyledContainer,
	StyledInput,
	StyledSendMessageButton,
} from './ChatInput.styled';

interface IChatInputProps {
	onMessageSend: () => void;
	onMouseEnter?: () => void;
}

const ChatInput: React.FC<IChatInputProps> = ({
	onMessageSend,
	onMouseEnter,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const sentMessages = useObservable(rage.chat.getSentMessages());
	const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
	const [temporaryInput, setTemporaryInput] = useState('');
	const [hasMessage, setHasMessage] = useState(false);

	const sendMessage = () => {
		const message = (inputRef.current?.value || '').trim();
		if (message) {
			setCurrentHistoryIndex(-1);
			setTemporaryInput('');
			setHasMessage(false);
			const promise = rage.chat.sendMessage(message);
			(inputRef.current as any).value = '';
			promise
				.finally(() => onMessageSend())
				.catch((err) => rage.notifications.showException(err));
			return;
		}

		onMessageSend();
	};

	const closeInput = () => {
		setCurrentHistoryIndex(-1);
		setTemporaryInput('');
		setHasMessage(false);

		(inputRef.current as any).value = '';

		onMessageSend();
	};

	const handleKeyDown = (direction: 'up' | 'down') => {
		if (direction === 'up') {
			if (sentMessages.length === 0) return;

			let newIndex: number;
			if (currentHistoryIndex === -1) {
				setTemporaryInput(inputRef.current?.value || '');
				newIndex = sentMessages.length - 1;
			} else {
				newIndex = Math.max(currentHistoryIndex - 1, 0);
			}

			if (newIndex !== currentHistoryIndex) {
				setCurrentHistoryIndex(newIndex);
				(inputRef.current as any).value = sentMessages[newIndex];
				setHasMessage(Boolean(sentMessages[newIndex].trim()));
				setTimeout(() => {
					inputRef.current?.setSelectionRange(
						sentMessages[newIndex].length,
						sentMessages[newIndex].length,
					);
				}, 0);
			}
		} else if (direction === 'down') {
			if (currentHistoryIndex === -1) return;

			const newIndex = currentHistoryIndex + 1;
			if (newIndex < sentMessages.length) {
				setCurrentHistoryIndex(newIndex);
				(inputRef.current as any).value = sentMessages[newIndex];
			} else {
				setCurrentHistoryIndex(-1);
				(inputRef.current as any).value = temporaryInput;
			}
			setHasMessage(Boolean((inputRef.current?.value || '').trim()));
			setTimeout(() => {
				inputRef.current?.setSelectionRange(
					sentMessages[newIndex].length,
					sentMessages[newIndex].length,
				);
			}, 0);
		}
	};

	useInputAction(
		InputAction.CommonMenuAccept,
		() => {
			sendMessage();
		},
		{ allowWhileFocused: true },
	);

	useInputAction(
		InputAction.CommonMenuClose,
		() => {
			closeInput();
		},
		{ allowWhileFocused: true },
	);

	useInputAction(InputAction.CommonMenuNavUp, () => handleKeyDown('up'), {
		allowWhileFocused: true,
	});
	useInputAction(InputAction.CommonMenuNavDown, () => handleKeyDown('down'), {
		allowWhileFocused: true,
	});

	useEffectOnce(() => {
		const timeout = setTimeout(() => inputRef.current?.focus(), 0);
		return () => clearTimeout(timeout);
	});

	return (
		<StyledContainer>
			<StyledInput
				ref={inputRef}
				onMouseEnter={onMouseEnter}
				onChange={(e) => setHasMessage(Boolean(e.target.value.trim()))}
				maxLength={300}
			/>
			<StyledSendMessageButton
				$active={hasMessage}
				onClick={() => {
					sendMessage();
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={onMouseEnter}
			>
				<img src={sendMessageIcon} />
			</StyledSendMessageButton>
		</StyledContainer>
	);
};

export { ChatInput };
